'use client';

import {
  ProcessorWrapper,
  type TrackTransformerDestroyOptions,
  type VideoTrackTransformer,
  type VideoTransformerInitOptions,
} from '@livekit/track-processors';

export type JewelryFilterId =
  | 'off'
  | 'brilliance'
  | 'warm-gold'
  | 'cool-silver'
  | 'contrast'
  | 'soft';

export type ColorBalance = {
  red: number;
  green: number;
  blue: number;
};

export type VideoEffectSettings = Record<string, unknown> & {
  brightness: number;
  contrast: number;
  saturation: number;
  warmth: number;
  beauty: number;
  red: number;
  green: number;
  blue: number;
};

type FilterSettings = Pick<
  VideoEffectSettings,
  'brightness' | 'contrast' | 'saturation' | 'warmth'
>;

export type JewelryFilterOption = {
  id: JewelryFilterId;
  label: string;
  description: string;
  previewClass: string;
  settings?: FilterSettings;
};

export const JEWELRY_FILTERS: JewelryFilterOption[] = [
  {
    id: 'off',
    label: '关闭',
    description: '保留原始画面',
    previewClass: 'filterPreviewOriginal',
  },
  {
    id: 'brilliance',
    label: '珠宝亮泽',
    description: '提亮并增强光泽',
    previewClass: 'filterPreviewBrilliance',
    settings: { brightness: 1.08, contrast: 1.18, saturation: 1.14, warmth: 0.02 },
  },
  {
    id: 'warm-gold',
    label: '暖金',
    description: '突出黄金暖色',
    previewClass: 'filterPreviewWarmGold',
    settings: { brightness: 1.05, contrast: 1.1, saturation: 1.12, warmth: 0.1 },
  },
  {
    id: 'cool-silver',
    label: '冷银',
    description: '突出银饰与钻石',
    previewClass: 'filterPreviewCoolSilver',
    settings: { brightness: 1.07, contrast: 1.14, saturation: 0.82, warmth: -0.08 },
  },
  {
    id: 'contrast',
    label: '高对比',
    description: '强化轮廓和细节',
    previewClass: 'filterPreviewContrast',
    settings: { brightness: 1, contrast: 1.3, saturation: 1.02, warmth: 0 },
  },
  {
    id: 'soft',
    label: '柔光',
    description: '明亮柔和不过曝',
    previewClass: 'filterPreviewSoft',
    settings: { brightness: 1.08, contrast: 0.92, saturation: 0.98, warmth: 0.025 },
  },
];

export const COLOR_BALANCE_MIN = 80;
export const COLOR_BALANCE_MAX = 120;
export const DEFAULT_COLOR_BALANCE: ColorBalance = { red: 100, green: 100, blue: 100 };
const BEAUTY_STRENGTH = 0.55;
const NEUTRAL_FILTER_SETTINGS: FilterSettings = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
  warmth: 0,
};

const VERTEX_SHADER = `
  attribute vec2 a_position;
  attribute vec2 a_texCoord;
  varying vec2 v_texCoord;

  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`;

const FRAGMENT_SHADER = `
  precision mediump float;

  uniform sampler2D u_image;
  uniform float u_brightness;
  uniform float u_contrast;
  uniform float u_saturation;
  uniform float u_warmth;
  uniform float u_beauty;
  uniform float u_red;
  uniform float u_green;
  uniform float u_blue;
  uniform vec2 u_texelSize;
  varying vec2 v_texCoord;

  void main() {
    vec4 source = texture2D(u_image, v_texCoord);
    vec3 color = source.rgb;

    if (u_beauty > 0.001) {
      vec3 smoothColor = color;
      smoothColor += texture2D(u_image, v_texCoord + vec2(u_texelSize.x, 0.0)).rgb;
      smoothColor += texture2D(u_image, v_texCoord - vec2(u_texelSize.x, 0.0)).rgb;
      smoothColor += texture2D(u_image, v_texCoord + vec2(0.0, u_texelSize.y)).rgb;
      smoothColor += texture2D(u_image, v_texCoord - vec2(0.0, u_texelSize.y)).rgb;
      smoothColor += texture2D(u_image, v_texCoord + u_texelSize).rgb;
      smoothColor += texture2D(u_image, v_texCoord - u_texelSize).rgb;
      smoothColor += texture2D(u_image, v_texCoord + vec2(u_texelSize.x, -u_texelSize.y)).rgb;
      smoothColor += texture2D(u_image, v_texCoord + vec2(-u_texelSize.x, u_texelSize.y)).rgb;
      smoothColor /= 9.0;

      float cb = -0.168736 * color.r - 0.331264 * color.g + 0.5 * color.b + 0.5;
      float cr = 0.5 * color.r - 0.418688 * color.g - 0.081312 * color.b + 0.5;
      float skin = smoothstep(0.30, 0.36, cr) * (1.0 - smoothstep(0.62, 0.70, cr));
      skin *= smoothstep(0.22, 0.30, cb) * (1.0 - smoothstep(0.54, 0.63, cb));
      float detail = clamp(length(color - smoothColor) * 5.0, 0.0, 1.0);
      float blendAmount = u_beauty * skin * (0.72 - detail * 0.26);
      color = mix(color, smoothColor, blendAmount);
      color += vec3(0.018, 0.012, 0.008) * u_beauty * skin;
    }

    color *= u_brightness;
    color = (color - 0.5) * u_contrast + 0.5;
    float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
    color = mix(vec3(luminance), color, u_saturation);
    color += vec3(u_warmth, u_warmth * 0.28, -u_warmth);
    color *= vec3(u_red, u_green, u_blue);
    gl_FragColor = vec4(clamp(color, 0.0, 1.0), source.a);
  }
`;

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('VIDEO_FILTER_SHADER_CREATE_FAILED');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'VIDEO_FILTER_SHADER_COMPILE_FAILED';
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

class JewelryFilterTransformer implements VideoTrackTransformer<VideoEffectSettings> {
  transformer?: TransformStream<VideoFrame, VideoFrame>;
  private canvas?: OffscreenCanvas | HTMLCanvasElement;
  private gl?: WebGLRenderingContext;
  private program?: WebGLProgram;
  private buffer?: WebGLBuffer;
  private texture?: WebGLTexture;
  private settings: VideoEffectSettings;

  constructor(settings: VideoEffectSettings) {
    this.settings = settings;
  }

  init({ outputCanvas }: VideoTransformerInitOptions) {
    this.canvas = outputCanvas;
    const gl = outputCanvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      preserveDrawingBuffer: false,
    }) as WebGLRenderingContext | null;
    if (!gl) throw new Error('VIDEO_FILTER_WEBGL_UNSUPPORTED');

    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!program) throw new Error('VIDEO_FILTER_PROGRAM_CREATE_FAILED');
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(program) || 'VIDEO_FILTER_PROGRAM_LINK_FAILED');
    }

    const buffer = gl.createBuffer();
    const texture = gl.createTexture();
    if (!buffer || !texture) throw new Error('VIDEO_FILTER_RESOURCE_CREATE_FAILED');

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 16, 8);

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);

    this.gl = gl;
    this.program = program;
    this.buffer = buffer;
    this.texture = texture;
    this.transformer = new TransformStream({
      transform: (frame, controller) => this.transform(frame, controller),
    });
  }

  restart(options: VideoTransformerInitOptions) {
    this.destroy({ willProcessorRestart: true });
    this.init(options);
  }

  destroy(_options?: TrackTransformerDestroyOptions) {
    if (this.gl) {
      if (this.texture) this.gl.deleteTexture(this.texture);
      if (this.buffer) this.gl.deleteBuffer(this.buffer);
      if (this.program) this.gl.deleteProgram(this.program);
    }
    this.transformer = undefined;
    this.canvas = undefined;
    this.gl = undefined;
    this.program = undefined;
    this.buffer = undefined;
    this.texture = undefined;
  }

  update(settings: VideoEffectSettings) {
    this.settings = settings;
  }

  transform(frame: VideoFrame, controller: TransformStreamDefaultController<VideoFrame>) {
    let sourceFrameEnqueued = false;
    try {
      if (
        !this.canvas ||
        !this.gl ||
        !this.program ||
        frame.codedWidth === 0 ||
        frame.codedHeight === 0
      ) {
        controller.enqueue(frame);
        sourceFrameEnqueued = true;
        return;
      }

      const gl = this.gl;
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.useProgram(this.program);
      gl.bindTexture(gl.TEXTURE_2D, this.texture ?? null);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        frame as unknown as TexImageSource,
      );
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_brightness'), this.settings.brightness);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_contrast'), this.settings.contrast);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_saturation'), this.settings.saturation);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_warmth'), this.settings.warmth);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_beauty'), this.settings.beauty);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_red'), this.settings.red);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_green'), this.settings.green);
      gl.uniform1f(gl.getUniformLocation(this.program, 'u_blue'), this.settings.blue);
      gl.uniform2f(
        gl.getUniformLocation(this.program, 'u_texelSize'),
        1 / this.canvas.width,
        1 / this.canvas.height,
      );
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      controller.enqueue(
        new VideoFrame(this.canvas, {
          timestamp: frame.timestamp ?? Math.round(performance.now() * 1000),
          duration: frame.duration ?? undefined,
        }),
      );
    } catch {
      controller.enqueue(frame);
      sourceFrameEnqueued = true;
    } finally {
      if (!sourceFrameEnqueued) frame.close();
    }
  }
}

export type JewelryFilterProcessor = ProcessorWrapper<
  VideoEffectSettings,
  JewelryFilterTransformer
>;

export function createJewelryFilterProcessor(settings: VideoEffectSettings) {
  if (!ProcessorWrapper.isSupported) throw new Error('VIDEO_FILTER_UNSUPPORTED');
  return new ProcessorWrapper<VideoEffectSettings, JewelryFilterTransformer>(
    new JewelryFilterTransformer(settings),
    'jewelry-video-effects',
    { maxFps: 30 },
  );
}

export function getJewelryFilterSettings(filterId: JewelryFilterId) {
  return JEWELRY_FILTERS.find((item) => item.id === filterId)?.settings;
}

function clampColorChannel(value: number) {
  if (!Number.isFinite(value)) return 100;
  return Math.min(COLOR_BALANCE_MAX, Math.max(COLOR_BALANCE_MIN, Math.round(value)));
}

export function normalizeColorBalance(balance: ColorBalance): ColorBalance {
  return {
    red: clampColorChannel(balance.red),
    green: clampColorChannel(balance.green),
    blue: clampColorChannel(balance.blue),
  };
}

export function hasActiveVideoEffect(
  filterId: JewelryFilterId,
  beautyEnabled: boolean,
  colorBalance: ColorBalance,
) {
  const normalized = normalizeColorBalance(colorBalance);
  return (
    filterId !== 'off' ||
    beautyEnabled ||
    normalized.red !== 100 ||
    normalized.green !== 100 ||
    normalized.blue !== 100
  );
}

export function createVideoEffectSettings(
  filterId: JewelryFilterId,
  beautyEnabled: boolean,
  colorBalance: ColorBalance,
): VideoEffectSettings {
  const filter = getJewelryFilterSettings(filterId) ?? NEUTRAL_FILTER_SETTINGS;
  const normalized = normalizeColorBalance(colorBalance);
  return {
    ...filter,
    beauty: beautyEnabled ? BEAUTY_STRENGTH : 0,
    red: normalized.red / 100,
    green: normalized.green / 100,
    blue: normalized.blue / 100,
  };
}
